import {
	type FullGoalDefinitionViewModel,
	type FullInterventionDefinitionViewModel,
	type GoalDefinitionViewModel,
	type GoalViewModel,
	type InterventionDefinitionViewModel,
	type InterventionViewModel
} from '@generated';
import type { FormValues } from '~/shared/forms/types';

export type SelectedGoalOption = 'new' | 'existing' | 'suggested' | 'custom';

export type SelectedInterventionOption = 'new' | 'suggested' | 'custom';

type WizardSteps =
	| 'SELECT_GOAL'
	| 'LOAD_GOAL_DEFINITION'
	| 'CAPTURE_GOAL_DETAILS'
	| 'SELECTING_INTERVENTIONS'
	| 'LOAD_INTERVENTION_DEFINITIONS'
	| 'CAPTURE_INTERVENTION_DETAILS';

type SelectedWizardGoal =
	| {
			type: 'predefined';
			newGoalId: string;
			goalDefinition: GoalDefinitionViewModel;
			fullGoalDefinition?: FullGoalDefinitionViewModel;
			formValues?: FormValues;
	  }
	| { type: 'custom'; newGoalId: string; formValues?: FormValues }
	| { type: 'existing'; existingGoal: GoalViewModel };

export interface NewPredefinedInterventionState {
	id: string;
	type: 'predefined';
	interventionDefinition: InterventionDefinitionViewModel;
	fullInterventionDefinition?: FullInterventionDefinitionViewModel;
	formValues?: FormValues;
}
export interface NewCustomInterventionState {
	id: string;
	type: 'custom';
	customInterventionNumber?: number;
	formValues?: FormValues;
}
export interface ExistingInterventionState {
	id: string;
	type: 'existing';
	existingIntervention: InterventionViewModel;
}

export type SelectedWizardIntervention = ExistingInterventionState | NewPredefinedInterventionState | NewCustomInterventionState;

export function sortSelectedInterventions<T extends SelectedWizardIntervention>(interventions: T[]): T[] {
	return interventions.toSorted((a, b) => {
		const aTitle =
			a.type === 'predefined'
				? a.interventionDefinition.title
				: a.type === 'custom'
				? `Custom #${a.customInterventionNumber}`
				: a.existingIntervention.title;
		const bTitle =
			b.type === 'predefined'
				? b.interventionDefinition.title
				: b.type === 'custom'
				? `Custom #${b.customInterventionNumber}`
				: b.existingIntervention.title;
		return aTitle.localeCompare(bTitle);
	});
}

export type WizardState = {
	currentStep: WizardSteps;
	selectedGoalOption: SelectedGoalOption;
	selectedGoalSearchTerm: string;
	selectedGoalSelectedCategories: string[];
	selectedGoal?: SelectedWizardGoal;
	hasSuggestedInterventions: boolean;
	selectedInterventionDiscipline: number;
	selectedInterventionCategories: string[];
	selectedInterventionOption: SelectedInterventionOption;
	selectedInterventions: SelectedWizardIntervention[];
	existingCustomInterventionCount: number;
};

export interface WizardReducer {
	wizardState: WizardState;
	dispatch: React.Dispatch<WizardAction>;
}

export function getInitialWizardState(
	hasSuggestedGoals: boolean,
	defaultAssignedDisciplineId: number,
	existingCustomInterventionCount: number
): WizardState {
	return {
		currentStep: 'SELECT_GOAL',
		selectedGoalOption: hasSuggestedGoals ? 'suggested' : 'new',
		selectedGoalSearchTerm: '',
		selectedGoalSelectedCategories: [],
		hasSuggestedInterventions: false,
		selectedInterventionOption: 'new',
		selectedInterventions: [],
		selectedInterventionCategories: [],
		selectedInterventionDiscipline: defaultAssignedDisciplineId,
		existingCustomInterventionCount
	};
}

export type WizardAction =
	//Goal Selection
	| { type: 'GOAL_OPTION_CHANGED'; option: SelectedGoalOption }
	| { type: 'EXISTING_GOAL_SELECTED'; goal: GoalViewModel; existingInterventions: InterventionViewModel[] }
	| {
			type: 'PREDEFINED_GOAL_SELECTED';
			newGoalId: string;
			goalDefinition: GoalDefinitionViewModel;
			goalInterventionDefinitions: InterventionDefinitionViewModel[];
			searchTerm: string;
			existingInterventions: InterventionViewModel[];
	  }
	| { type: 'CUSTOM_GOAL_SELECTED'; newGoalId: string }
	| { type: 'GOAL_CATEGORY_TOGGLED'; category: string }
	//Goal Loading
	| { type: 'FULL_GOAL_DEFINITION_LOADED'; fullGoalDefinition: FullGoalDefinitionViewModel }
	//Goal Details
	| { type: 'PREDEFINED-GOAL-DETAILS-ENTERED'; formValues: FormValues; fullGoalDefinition: FullGoalDefinitionViewModel }
	| { type: 'CUSTOM_GOAL_DETAILS_ENTERED'; formValues: FormValues }
	//Intervention Selection
	| { type: 'INTERVENTION_OPTION_CHANGED'; option: SelectedInterventionOption }
	| {
			type: 'PREDEFINED_INTERVENTION_SELECTED';
			newInterventionId: string;
			interventionDefinition: InterventionDefinitionViewModel;
			updateInterventionOption?: SelectedInterventionOption;
	  }
	| { type: 'INTERVENTION_UNSELECTED'; interventionId: string }
	| { type: 'CUSTOM_INTERVENTION_SELECTED'; newInterventionId: string }
	| { type: 'INTERVENTION_CATEGORY_TOGGLED'; category: string }
	| { type: 'INTERVENTION_DISCIPLINE_CHANGED'; disciplineId: number }
	//Intervention Loading
	| { type: 'FULL_INTERVENTION_DEFINITION_LOADED'; fullInterventionDefinitions: FullInterventionDefinitionViewModel[] }
	//Wizard Navigation
	| { type: 'NEXT_CLICKED' }
	| { type: 'PREVIOUS_CLICKED' }
	| { type: 'STEP_CLICKED'; step: WizardState['currentStep'] };

function goalSelectionWizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'GOAL_OPTION_CHANGED':
			return { ...state, selectedGoalOption: action.option, selectedGoalSelectedCategories: [] };
		case 'EXISTING_GOAL_SELECTED':
			return {
				...state,
				currentStep: 'SELECTING_INTERVENTIONS',
				selectedGoal: { type: 'existing', existingGoal: action.goal },
				selectedInterventionOption: 'new',
				selectedInterventions: []
			};
		case 'PREDEFINED_GOAL_SELECTED': {
			const goalProcedureIds = action.goalDefinition.goalProcedures.map(x => x.procedureConceptId);
			const existingInterventions = action.existingInterventions.filter(
				x =>
					x.interventionDefinition &&
					x.interventionDefinition.procedureConceptId &&
					goalProcedureIds.includes(x.interventionDefinition.procedureConceptId)
			);
			const existingInterventionProcedureIds = existingInterventions.map(x => x.interventionDefinition!.procedureConceptId!);
			const newInterventions = action.goalInterventionDefinitions.filter(
				x => x.procedureConceptId && !existingInterventionProcedureIds.includes(x.procedureConceptId)
			);
			return {
				...state,
				currentStep: action.goalDefinition.hasDefinitionFields ? 'LOAD_GOAL_DEFINITION' : 'SELECTING_INTERVENTIONS',
				selectedGoalSearchTerm: action.searchTerm,
				selectedGoal: { type: 'predefined', newGoalId: action.newGoalId, goalDefinition: action.goalDefinition },
				selectedInterventionOption: 'new',
				selectedInterventions: [
					...newInterventions.map((x): SelectedWizardIntervention => ({ type: 'predefined', id: x.id, interventionDefinition: x })),
					...existingInterventions.map((x): SelectedWizardIntervention => ({ type: 'existing', id: x.id, existingIntervention: x }))
				]
			};
		}
		case 'CUSTOM_GOAL_SELECTED':
			return {
				...state,
				currentStep: 'CAPTURE_GOAL_DETAILS',
				selectedGoal: { type: 'custom', newGoalId: action.newGoalId },
				selectedInterventionOption: 'new',
				selectedInterventions: []
			};
		case 'GOAL_CATEGORY_TOGGLED':
			if (state.selectedGoalSelectedCategories.includes(action.category)) {
				return { ...state, selectedGoalSelectedCategories: state.selectedGoalSelectedCategories.filter(x => x !== action.category) };
			} else {
				return { ...state, selectedGoalSelectedCategories: [...state.selectedGoalSelectedCategories, action.category] };
			}
		default:
			return state;
	}
}

function goalDetailsWizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'FULL_GOAL_DEFINITION_LOADED':
			if (!state.selectedGoal || state.selectedGoal.type !== 'predefined') {
				throw new Error('Invalid action');
			}
			return {
				...state,
				currentStep: 'CAPTURE_GOAL_DETAILS',
				selectedGoal: { ...state.selectedGoal, fullGoalDefinition: action.fullGoalDefinition }
			};
		case 'PREDEFINED-GOAL-DETAILS-ENTERED':
			if (!state.selectedGoal || state.selectedGoal.type !== 'predefined') {
				throw new Error('Invalid action');
			}
			return {
				...state,
				currentStep: 'SELECTING_INTERVENTIONS',
				selectedGoal: {
					...state.selectedGoal,
					formValues: action.formValues,
					fullGoalDefinition: action.fullGoalDefinition
				}
			};
		case 'CUSTOM_GOAL_DETAILS_ENTERED':
			if (state.selectedGoalOption !== 'custom') {
				throw new Error('Invalid action');
			}
			return {
				...state,
				currentStep: 'SELECTING_INTERVENTIONS',
				selectedGoal: {
					type: 'custom',
					formValues: action.formValues,
					newGoalId: 'custom'
				}
			};
		default:
			return state;
	}
}

function interventionSelectionWizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'INTERVENTION_OPTION_CHANGED':
			return { ...state, selectedInterventionOption: action.option };
		case 'PREDEFINED_INTERVENTION_SELECTED':
			return {
				...state,
				selectedInterventionOption: action.updateInterventionOption ?? state.selectedInterventionOption,
				selectedInterventions: [
					...state.selectedInterventions,
					{ type: 'predefined', id: action.newInterventionId, interventionDefinition: action.interventionDefinition }
				]
			};
		case 'CUSTOM_INTERVENTION_SELECTED':
			return {
				...state,
				selectedInterventions: [...state.selectedInterventions, { type: 'custom', id: action.newInterventionId }]
			};
		case 'INTERVENTION_UNSELECTED':
			return {
				...state,
				selectedInterventions: state.selectedInterventions.filter(x => x.id !== action.interventionId)
			};
		case 'INTERVENTION_CATEGORY_TOGGLED':
			if (state.selectedInterventionCategories.includes(action.category)) {
				return { ...state, selectedInterventionCategories: state.selectedInterventionCategories.filter(x => x !== action.category) };
			} else {
				return { ...state, selectedInterventionCategories: [...state.selectedInterventionCategories, action.category] };
			}
		case 'INTERVENTION_DISCIPLINE_CHANGED':
			return { ...state, selectedInterventionDiscipline: action.disciplineId };
		case 'FULL_INTERVENTION_DEFINITION_LOADED':
			return {
				...state,
				currentStep: 'CAPTURE_INTERVENTION_DETAILS',
				selectedInterventions: state.selectedInterventions.map(x => {
					if (x.type === 'predefined' && x.interventionDefinition.hasDefinitionFields && !x.fullInterventionDefinition) {
						const fullDefinition = action.fullInterventionDefinitions.find(y => y.id === x.interventionDefinition.id);
						if (!fullDefinition) {
							throw new Error('Full intervention definition not found');
						}
						return { ...x, fullInterventionDefinition: fullDefinition };
					}
					return x;
				})
			};
		default:
			return state;
	}
}

function wizardNavigationReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'STEP_CLICKED':
			return { ...state, currentStep: action.step };
		case 'PREVIOUS_CLICKED': {
			if (state.currentStep === 'SELECT_GOAL') {
				throw new Error('Invalid action');
			} else if (!state.selectedGoal) {
				throw new Error('Invalid action');
			} else if (state.currentStep === 'CAPTURE_GOAL_DETAILS') {
				return {
					...state,
					currentStep: 'SELECT_GOAL'
				};
			} else if (state.currentStep === 'SELECTING_INTERVENTIONS') {
				if (
					state.selectedGoal.type === 'existing' ||
					state.selectedGoal.type === 'custom' ||
					(state.selectedGoal.type === 'predefined' && !state.selectedGoal.goalDefinition.hasDefinitionFields)
				) {
					return {
						...state,
						currentStep: 'SELECT_GOAL'
					};
				}
				return {
					...state,
					currentStep: 'CAPTURE_GOAL_DETAILS'
				};
			} else if (state.currentStep === 'LOAD_INTERVENTION_DEFINITIONS' || state.currentStep === 'CAPTURE_INTERVENTION_DETAILS') {
				return {
					...state,
					currentStep: 'SELECTING_INTERVENTIONS'
				};
			}
			break;
		}
		case 'NEXT_CLICKED': {
			if (!state.selectedGoal) {
				throw new Error('Invalid action');
			}
			if (state.currentStep === 'CAPTURE_GOAL_DETAILS') {
				return {
					...state,
					currentStep: 'SELECTING_INTERVENTIONS'
				};
			} else if (state.currentStep === 'SELECTING_INTERVENTIONS') {
				let currentInterventionCount =
					state.existingCustomInterventionCount +
					state.selectedInterventions.filter(x => x.type === 'custom' && x.customInterventionNumber).length;
				const selectedInterventions: SelectedWizardIntervention[] = [...state.selectedInterventions];
				for (let i = 0; i < selectedInterventions.length; i++) {
					const intervention = selectedInterventions[i];
					if (intervention.type === 'custom' && !intervention.customInterventionNumber) {
						currentInterventionCount++;
						selectedInterventions[i] = { ...intervention, customInterventionNumber: currentInterventionCount };
					}
				}

				const requiresDefinitionLoading = selectedInterventions.some(
					x => x.type === 'predefined' && x.interventionDefinition.hasDefinitionFields && !x.fullInterventionDefinition
				);
				return {
					...state,
					currentStep: requiresDefinitionLoading ? 'LOAD_INTERVENTION_DEFINITIONS' : 'CAPTURE_INTERVENTION_DETAILS',
					selectedInterventions
				};
			}
		}
	}
	return state;
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'GOAL_OPTION_CHANGED':
		case 'EXISTING_GOAL_SELECTED':
		case 'PREDEFINED_GOAL_SELECTED':
		case 'CUSTOM_GOAL_SELECTED':
		case 'GOAL_CATEGORY_TOGGLED':
			return goalSelectionWizardReducer(state, action);
		case 'FULL_GOAL_DEFINITION_LOADED':
		case 'PREDEFINED-GOAL-DETAILS-ENTERED':
		case 'CUSTOM_GOAL_DETAILS_ENTERED':
			return goalDetailsWizardReducer(state, action);
		case 'INTERVENTION_OPTION_CHANGED':
		case 'PREDEFINED_INTERVENTION_SELECTED':
		case 'CUSTOM_INTERVENTION_SELECTED':
		case 'INTERVENTION_UNSELECTED':
		case 'INTERVENTION_CATEGORY_TOGGLED':
		case 'INTERVENTION_DISCIPLINE_CHANGED':
		case 'FULL_INTERVENTION_DEFINITION_LOADED':
			return interventionSelectionWizardReducer(state, action);
		case 'STEP_CLICKED':
		case 'PREVIOUS_CLICKED':
		case 'NEXT_CLICKED': {
			return wizardNavigationReducer(state, action);
		}
	}
}
